import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Plus, Volume2, VolumeX, User, LogOut, Copy, Archive, Check, X, HelpCircle, Music2, Smile, Pin, PinOff, Inbox, Share2, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl } from "@/lib/api";
import { logError } from "@/lib/logger";
import { useBoard } from "@/hooks/useBoard";
import { getTheme, themeList } from "@/lib/themes";
import { isMuted, setMuted } from "@/lib/sounds";
import StickyNote from "@/components/StickyNote";
import Polaroid from "@/components/Polaroid";
import { Lightbox } from "@/components/Lightbox";
import { NoteDialog } from "@/components/NoteDialog";
import { ComposeDialog } from "@/components/ComposeDialog";
import { EmojiDialog } from "@/components/EmojiDialog";
import { SoundSettings } from "@/components/SoundSettings";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

const NOTE_COLORS = ["yellow", "pink", "blue", "green", "peach", "lilac"];

export default function BoardPage() {
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuth();
  const boardRef = useRef(null);
  const fileRef = useRef(null);
  const [expanded, setExpanded] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [compose, setCompose] = useState(false);
  const [composePrefill, setComposePrefill] = useState("");
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [smsNumber, setSmsNumber] = useState(null);
  const [digest, setDigest] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);

  useEffect(() => {
    api.get("/config").then(({ data }) => setSmsNumber(data.sms_number)).catch(() => {});
    api.get("/channels/digest").then(({ data }) => setDigest(data)).catch(() => {});
  }, []);

  const [soundOpen, setSoundOpen] = useState(false);
  const [aiModel, setAiModel] = useState("gpt-5.4");
  const [muted, setMutedState] = useState(isMuted());
  const [showHelp, setShowHelp] = useState(localStorage.getItem("pm_help_seen") !== "1");
  const [coach, setCoach] = useState(() => localStorage.getItem("pm_coach_done") !== "1");

  const addBlank = async (c) => {
    const note = await createBlankNote(c);
    if (note) { bringFront(note.id); setEditingNoteId(note.id); }
  };
  const addBlankRef = useRef(addBlank);
  addBlankRef.current = addBlank;

  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (compose || emojiOpen || soundOpen || expanded || lightbox) return;
      if (e.key === "n" || e.key === "N") { e.preventDefault(); addBlankRef.current("yellow"); }
      else if (e.key >= "1" && e.key <= "6") { e.preventDefault(); addBlankRef.current(NOTE_COLORS[Number(e.key) - 1]); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [compose, emojiOpen, soundOpen, expanded, lightbox]);

  const {
    notes, polaroids, doneNotes,
    bringFront, noteMove, completeNote, deleteNote, createNote, createBlankNote, updateNoteText,
    polMove, polCaption, polDelete, uploadPolaroid,
    changeTheme, openDonePile, restoreNote,
    aiAction, aiDoodle,
    transcribeAudio, transcribeVideo, createEmoji, blockSender, tidyBoard,
  } = useBoard(user, setUser);

  const handleBlock = async (sender) => {
    try {
      const blocked = await blockSender(sender);
      setUser({ ...user, blocked });
      toast.success("Sender blocked — their notes are gone.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not block");
    }
  };

  const theme = getTheme(user?.theme);
  const ink = theme.dark ? "#fdf6e3" : "#3a2f22";
  const avatarSrc = user?.avatar_url ? fileUrl(user.avatar_url) : user?.picture;
  const pinned = user?.pin_profile !== false;
  const togglePin = async () => {
    try {
      const { data } = await api.patch("/profile", { pin_profile: !pinned });
      setUser(data);
      toast.success(!pinned ? "Profile photo pinned to your board" : "Profile photo unpinned");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) await uploadPolaroid(file);
  };

  const toggleMute = () => { const m = !muted; setMuted(m); setMutedState(m); };
  const copyAddress = () => { navigator.clipboard?.writeText(`${user.handle}@pinanote.app`); toast.success("Address copied!"); };

  const shareBoard = async () => {
    const el = boardRef.current;
    if (!el) return;
    toast("Creating your board image…");
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(el, { backgroundColor: "#c9a876", scale: 2, useCORS: true, logging: false });
      canvas.toBlob(async (blob) => {
        if (!blob) { toast.error("Couldn't create image"); return; }
        const file = new File([blob], "my-pin-a-note-board.png", { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try { await navigator.share({ files: [file], title: "My Pin a Note board", text: "My messages as sticky notes 📌 pinanote.app" }); return; } catch { /* fall back to download */ }
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "my-pin-a-note-board.png"; a.click();
        URL.revokeObjectURL(url);
        toast.success("Board image saved — share it anywhere!");
      }, "image/png");
    } catch (e) {
      logError("share board failed:", e);
      toast.error("Couldn't create image (a photo may be blocking it)");
    }
  };
  const dismissHelp = () => { setShowHelp(false); localStorage.setItem("pm_help_seen", "1"); };

  const finishCoach = () => { setCoach(false); localStorage.setItem("pm_coach_done", "1"); };
  const handleCreateNote = async (payload) => {
    await createNote(payload);
    if (coach) { finishCoach(); toast.success("🎉 You sent your first note — it's on your board!"); }
  };

  const handleVideoTranscribe = async (pol) => {
    const tid = toast.loading("Transcribing video…");
    try {
      const text = await transcribeVideo(pol.id);
      toast.dismiss(tid);
      if (!text) { toast.message("No speech detected in this video"); return; }
      setLightbox(null);
      setComposePrefill(text);
      setCompose(true);
    } catch (e) {
      toast.dismiss(tid);
      toast.error(e?.response?.data?.detail || "Transcription failed");
    }
  };

  const closeCompose = () => { setCompose(false); setComposePrefill(""); };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: theme.frame }}>
      {/* Header */}
      <header className="relative z-30 flex items-center justify-between px-4 sm:px-6 py-3" style={{ color: ink }}>
        <div className="flex items-center gap-3">
          <span className="font-display font-black text-xl tracking-tight" style={{ color: ink }}>Pin a Note</span>
          <button data-testid="copy-address" onClick={copyAddress} className="hidden sm:flex items-center gap-1.5 bg-white/85 hover:bg-white text-[#3a2f22] rounded-full px-3 py-1 text-sm font-note font-bold">
            {user?.handle}@pinanote.app <Copy className="w-3.5 h-3.5" />
          </button>
          {digest?.total > 0 && (
            <div data-testid="board-digest" title={Object.entries(digest.counts || {}).map(([k, v]) => `${v} ${k}`).join(" · ")} className="hidden md:flex items-center gap-1.5 bg-white/85 text-[#3a2f22] rounded-full px-3 py-1 text-sm font-note font-bold">
              <Inbox className="w-3.5 h-3.5" /> {digest.total} this week
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept="image/*,video/*" onChange={onUpload} className="hidden" data-testid="upload-input" />
          <Button data-testid="upload-btn" onClick={() => fileRef.current?.click()} size="sm" className="rounded-full bg-white/90 hover:bg-white text-[#3a2f22] font-display font-bold gap-1">
            <Camera className="w-4 h-4" /> <span className="hidden sm:inline">Photo/Video</span>
          </Button>
          <Button data-testid="share-board-btn" onClick={shareBoard} size="sm" className="rounded-full bg-white/90 hover:bg-white text-[#3a2f22] font-display font-bold gap-1">
            <Share2 className="w-4 h-4" /> <span className="hidden sm:inline">Share</span>
          </Button>
          <div className="relative">
            <Button data-testid="compose-btn" onClick={() => setCompose(true)} size="sm" className={`rounded-full bg-[#ff3b30] hover:bg-[#e0271d] text-white font-display font-bold gap-1 ${!showHelp && coach ? "ring-4 ring-[#ff3b30]/40 animate-pulse" : ""}`}>
              <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Note</span>
            </Button>
            <AnimatePresence>
              {!showHelp && coach && (
                <motion.div
                  data-testid="coach-note"
                  initial={{ opacity: 0, y: -6, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -6 }}
                  className="absolute top-full right-0 mt-2.5 w-60 bg-white rounded-xl p-3 paper-shadow-lift z-50 text-left"
                >
                  <span className="absolute -top-1.5 right-8 w-3 h-3 bg-white rotate-45" />
                  <p className="font-display font-black text-sm text-[#2b2620]">Send your first note ✍️</p>
                  <p className="text-xs text-[#6b6155] mt-0.5 leading-snug">Tap <b>Note</b> to type or speak a message — pick Email or SMS and it pins to your board like real mail.</p>
                  <button data-testid="coach-dismiss" onClick={finishCoach} className="mt-2 text-xs font-display font-bold text-[#a0703c] hover:text-[#2b2620]">Got it</button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <Button data-testid="emoji-btn" onClick={() => setEmojiOpen(true)} size="sm" className="rounded-full bg-white/90 hover:bg-white text-[#3a2f22] font-display font-bold gap-1">
            <Smile className="w-4 h-4" /> <span className="hidden sm:inline">Emoji</span>
          </Button>

          <Button data-testid="tidy-btn" onClick={tidyBoard} size="sm" variant="ghost" className="rounded-full text-current hover:bg-white/20 font-display font-bold gap-1" title="Tidy up the board">
            <LayoutGrid className="w-4 h-4" /> <span className="hidden lg:inline">Tidy up</span>
          </Button>

          <Sheet onOpenChange={(o) => o && openDonePile()}>
            <SheetTrigger asChild>
              <Button data-testid="done-pile-btn" size="icon" variant="ghost" className="rounded-full text-current hover:bg-white/20"><Archive className="w-5 h-5" /></Button>
            </SheetTrigger>
            <SheetContent data-testid="done-pile" className="bg-[#fbf6ec] grain overflow-auto">
              <SheetHeader><SheetTitle className="font-display font-black text-[#2b2620]">Done pile</SheetTitle></SheetHeader>
              <p className="text-sm text-[#6b6155] mb-4">Completed notes stick around for 7 days, then vanish.</p>
              <div className="space-y-3">
                {doneNotes.length === 0 && <p className="font-hand text-2xl text-[#b7a98f]">Nothing crumpled yet.</p>}
                {doneNotes.map((n) => (
                  <div key={n.id} className={`note-${n.color} rounded-[3px] p-3 paper-shadow`}>
                    <p className="font-note font-bold text-sm text-[#2b2620]">{n.subject || n.sender_name}</p>
                    <p className="font-note text-sm text-[#4a4238] line-clamp-2">{n.body}</p>
                    <button data-testid={`restore-${n.id}`} onClick={() => restoreNote(n.id)} className="mt-1 text-xs font-display font-bold text-[#007aff]">Un-crumple ↩</button>
                  </div>
                ))}
              </div>
            </SheetContent>
          </Sheet>

          <Button data-testid="mute-btn" onClick={toggleMute} size="icon" variant="ghost" className="rounded-full text-current hover:bg-white/20">
            {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </Button>

          <Button data-testid="sound-settings-btn" onClick={() => setSoundOpen(true)} size="icon" variant="ghost" className="rounded-full text-current hover:bg-white/20" title="Sound pack">
            <Music2 className="w-5 h-5" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="theme-menu" className="rounded-full w-9 h-9 border-2 border-white/60 overflow-hidden" style={{ background: theme.swatch }} title="Theme" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel className="font-display">Pick your board</DropdownMenuLabel>
              {themeList.map((t) => (
                <DropdownMenuItem data-testid={`theme-${t.key}`} key={t.key} onClick={() => changeTheme(t.key)} className="gap-2 cursor-pointer">
                  <span className="w-5 h-5 rounded-full border" style={{ background: t.swatch }} />
                  <div><p className="font-bold text-sm leading-none">{t.name}</p><p className="text-[11px] text-muted-foreground">{t.desc}</p></div>
                  {user?.theme === t.key && <Check className="w-4 h-4 ml-auto" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu" className="rounded-full w-9 h-9 bg-white/90 flex items-center justify-center text-[#3a2f22] overflow-hidden">
                {avatarSrc ? <img src={avatarSrc} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5" />}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="font-display truncate">{user?.name}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="nav-profile" onClick={() => navigate("/profile")} className="cursor-pointer"><User className="w-4 h-4 mr-2" />Profile & stats</DropdownMenuItem>
              <DropdownMenuItem data-testid="toggle-pin-profile" onClick={togglePin} className="cursor-pointer">{pinned ? <PinOff className="w-4 h-4 mr-2" /> : <Pin className="w-4 h-4 mr-2" />}{pinned ? "Unpin profile photo" : "Pin profile photo"}</DropdownMenuItem>
              <DropdownMenuItem data-testid="help-again" onClick={() => setShowHelp(true)} className="cursor-pointer"><HelpCircle className="w-4 h-4 mr-2" />How to use</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-btn" onClick={async () => { await logout(); navigate("/"); }} className="cursor-pointer text-[#ff3b30]"><LogOut className="w-4 h-4 mr-2" />Sign out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Board */}
      <div
        ref={boardRef}
        data-testid="board"
        className="relative grain mx-2 sm:mx-3 mb-3 rounded-xl board-scroll"
        style={{ height: "calc(100vh - 74px)", backgroundImage: theme.board, backgroundColor: theme.swatch, backgroundSize: "cover", backgroundPosition: "center", boxShadow: "inset 0 0 60px rgba(0,0,0,.35), inset 0 0 0 6px rgba(0,0,0,.12)", overflow: "hidden" }}
      >
        <AnimatePresence>
          {showHelp && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="absolute top-4 left-1/2 -translate-x-1/2 z-40 bg-white/95 backdrop-blur-xl rounded-2xl px-5 py-4 paper-shadow max-w-sm" data-testid="help-banner">
              <button onClick={dismissHelp} className="absolute top-2 right-2 text-[#6b6155] hover:text-[#2b2620]"><X className="w-4 h-4" /></button>
              <p className="font-display font-black text-[#2b2620] mb-1">Welcome to your board 👋</p>
              <ol className="text-sm text-[#4a4238] space-y-1 list-decimal ml-4">
                <li><b>Drag</b> notes anywhere. <b>Tap</b> to read.</li>
                <li><b>Fling right</b> (or hit ✓) to crumple a note done.</li>
                <li>Tap <b>Photo</b> to pin a polaroid; caption & rotate it.</li>
                <li>Change your <b>board theme</b> from the color chip.</li>
                <li>Emails to <b>{user?.handle}@pinanote.app</b> arrive automatically.</li>
              </ol>
              <Button data-testid="help-compose" onClick={() => { dismissHelp(); setCompose(true); }} className="mt-3 w-full rounded-full bg-[#ff3b30] hover:bg-[#e0271d] text-white font-display font-bold h-9">Try adding a note</Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Your pinned profile polaroid — click to set up your profile & photo */}
        {pinned && (
        <div
          data-testid="board-profile-polaroid"
          onClick={() => navigate("/profile")}
          className="group absolute top-4 left-4 z-20 bg-white p-2 pb-5 paper-shadow rotate-[-5deg] w-[118px] cursor-pointer hover:rotate-0 transition-transform duration-200"
          title="Your profile"
        >
          <span className="thumbtack" />
          <button
            data-testid="unpin-profile"
            onClick={(e) => { e.stopPropagation(); togglePin(); }}
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white paper-shadow flex items-center justify-center text-[#5a5148] hover:text-[#ff3b30] opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
            title="Unpin from board"
          >
            <PinOff className="w-3.5 h-3.5" />
          </button>
          <div className="w-full aspect-square bg-[#efeae2] overflow-hidden flex items-center justify-center">
            {avatarSrc ? (
              <img src={avatarSrc} alt={user?.name} className="w-full h-full object-cover" />
            ) : (
              <div className="flex flex-col items-center text-[#b7a98f]">
                <Camera className="w-6 h-6" />
                <span className="text-[10px] font-display font-bold mt-1">Add photo</span>
              </div>
            )}
          </div>
          <p className="font-hand text-base text-center text-[#3a2f22] leading-tight mt-1 truncate">{user?.name || "You"}</p>
        </div>
        )}

        {polaroids.map((p) => (
          <Polaroid key={p.id} pol={p} boardRef={boardRef} onOpen={setLightbox} onDelete={polDelete} onPositionChange={polMove} onBringFront={bringFront} onCaption={polCaption} />
        ))}
        <AnimatePresence>
          {notes.map((n) => (
            <StickyNote key={n.id} note={n} boardRef={boardRef} onComplete={completeNote} onExpand={setExpanded} onPositionChange={noteMove} onBringFront={bringFront}
              editing={editingNoteId === n.id} onEditStart={setEditingNoteId} onSaveText={updateNoteText} onEditEnd={() => setEditingNoteId(null)} />
          ))}
        </AnimatePresence>

        {/* Fresh blank note palette — tap a colour (or press N / 1-6) to drop a note and type */}
        <div data-testid="note-palette" className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5 bg-white/85 backdrop-blur rounded-full px-3 py-2 paper-shadow">
          <span className="text-xs font-display font-bold text-[#6b6155] mr-1 hidden sm:inline">New note</span>
          {NOTE_COLORS.map((c) => (
            <button key={c} data-testid={`palette-${c}`} onClick={() => addBlank(c)} title={`Blank ${c} note`}
              className={`note-${c} w-7 h-7 rounded-[3px] paper-shadow hover:scale-110 transition-transform`} />
          ))}
        </div>

        {notes.length === 0 && polaroids.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="font-hand text-4xl" style={{ color: theme.dark ? "rgba(253,246,227,.5)" : "rgba(58,47,34,.4)" }}>Your board is empty — pin a note!</p>
          </div>
        )}
      </div>

      <NoteDialog note={expanded} onClose={() => setExpanded(null)} onComplete={completeNote} onDelete={deleteNote} onBlock={handleBlock} model={aiModel} setModel={setAiModel} onAi={aiAction} onDoodle={aiDoodle} />
      <Lightbox pol={lightbox} onClose={() => setLightbox(null)} onDelete={polDelete} onTranscribe={handleVideoTranscribe} />
      <ComposeDialog open={compose} onClose={closeCompose} onCreate={handleCreateNote} onTranscribe={transcribeAudio} prefillBody={composePrefill} handle={user?.handle} isPro={user?.tier !== "free"} smsNumber={smsNumber} />
      <EmojiDialog open={emojiOpen} onClose={() => setEmojiOpen(false)} onCreate={createEmoji} />
      <SoundSettings open={soundOpen} onClose={() => setSoundOpen(false)} />
    </div>
  );
}
